import type { EstadoCivilSchemaInput } from '@/features/proposta/brasil/domain/schemas/compradores/common'
import type {
  CompradorPessoaFisicaStepSchemaOutput,
  CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type { CompradorPessoaJuridicaSchemaOutput } from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pj.schema'
import {
  PapelCompradorPF,
  type TPapelComprador,
  type TPapelCompradorPF,
} from '@/features/proposta/brasil/types/proposta.type'
import { EstadoCivilEnum, tipoDeCliente, type TCreateCadastrarClienteData } from '@/types/cliente'
import type { TOrigemTelefone } from '@/utils/schemas/telefone.schema'

const formatarTelefonePrincipal = (tel: { numero: string; origem: TOrigemTelefone }) => ({
  numero: tel.numero.replaceAll(/\D/g, ''),
  origem: tel.origem,
  principal: true,
})

export const obterIdPorTipo = (
  papel: TPapelComprador,
  formValue:
    | CompradorPessoaFisicaStepSchemaOutput
    | CompradorPessoaJuridicaSchemaOutput
    | CompradorVinculoStepSchemaOutput,
): string => {
  switch (papel) {
    case PapelCompradorPF.CONJUGE:
      return (formValue as CompradorPessoaFisicaStepSchemaOutput).conjuge!.dados.id!
    case PapelCompradorPF.MAE_PAI:
      return (formValue as CompradorPessoaFisicaStepSchemaOutput).maePai!.dados.id!
    case PapelCompradorPF.REPRESENTANTE:
    case PapelCompradorPF.PROCURADOR:
      return (formValue as CompradorPessoaJuridicaSchemaOutput).responsavel!.dados.id!
    default:
      return formValue.dados.id!
  }
}
type StrategyFn = (comprador: CompradorPessoaFisicaStepSchemaOutput) => TCreateCadastrarClienteData

const normalizarEstadoCivil = (
  estadoCivil: EstadoCivilSchemaInput | CompradorPessoaFisicaStepSchemaOutput['estadoCivil'],
) => {
  if (estadoCivil?.status !== 'CivilCasado') {
    return {
      estadoCivil: estadoCivil?.status,
      possuiUniaoEstavel: estadoCivil?.uniaoEstavel === 'sim',
      regimeCasamento: null,
    }
  }
  return {
    estadoCivil: EstadoCivilEnum.CivilCasado,
    possuiUniaoEstavel: null,
    regimeCasamento: estadoCivil?.regimeCasamento,
  }
}

const mapComprador: StrategyFn = (comprador) => {
  const isMenor = comprador.dados.maiorDe18Anos !== 'sim'
  const cadastrandoMenorComMaePai = isMenor && comprador.maePai && comprador.mode === 'create'
  if (cadastrandoMenorComMaePai) {
    return {
      cpf: comprador.dados.cpf.replaceAll(/\D/g, ''),
      nome: comprador.dados.nome,
      nacionalidadeId: comprador.dados.nacionalidadeId,
      profissaoId: comprador.dados.profissaoId,
      telefone: formatarTelefonePrincipal(comprador.maePai!.contato.telefone),
      email: comprador.dados.email || comprador.maePai!.contato.email,
      estadoCivil: EstadoCivilEnum.CivilSolteiro,
      possuiUniaoEstavel: false,
      regimeCasamento: null,
      maiorDe18Anos: false,
      tipoDoCliente: tipoDeCliente.Contato,
    }
  }

  const { status, possuiUniaoEstavel, regimeCasamento } = comprador.estadoCivil!
  return {
    cpf: comprador.dados.cpf.replaceAll(/\D/g, ''),
    nome: comprador.dados.nome,
    nacionalidadeId: comprador.dados.nacionalidadeId,
    profissaoId: comprador.dados.profissaoId,
    telefone: formatarTelefonePrincipal(comprador.contato!.telefone),
    email: isMenor ? comprador.dados.email! : comprador.contato!.email,
    estadoCivil: status ?? null,
    possuiUniaoEstavel,
    regimeCasamento,
    maiorDe18Anos: !isMenor,
    tipoDoCliente: tipoDeCliente.Contato,
  }
}

const mapConjuge: StrategyFn = (comprador) => {
  const conjuge = comprador.conjuge!
  const { estadoCivil, possuiUniaoEstavel, regimeCasamento } = normalizarEstadoCivil(comprador.estadoCivil)

  return {
    cpf: conjuge.dados.cpf.replaceAll(/\D/g, ''),
    nome: conjuge.dados.nome,
    nacionalidadeId: conjuge.dados.nacionalidadeId,
    profissaoId: conjuge.dados.profissaoId,
    telefone: formatarTelefonePrincipal(conjuge.contato.telefone),
    email: conjuge.contato.email,
    estadoCivil: estadoCivil ?? null,
    possuiUniaoEstavel: possuiUniaoEstavel,
    regimeCasamento: regimeCasamento ?? null,
    maiorDe18Anos: true,
    tipoDoCliente: tipoDeCliente.Contato,
  }
}

const mapMaePai: StrategyFn = (comprador) => {
  const maePai = comprador.maePai!
  const { estadoCivil, possuiUniaoEstavel, regimeCasamento } = normalizarEstadoCivil(maePai.estadoCivil)

  return {
    cpf: maePai.dados.cpf.replaceAll(/\D/g, ''),
    nome: maePai.dados.nome,
    nacionalidadeId: maePai.dados.nacionalidadeId,
    profissaoId: maePai.dados.profissaoId,
    telefone: formatarTelefonePrincipal(maePai.contato.telefone),
    email: maePai.contato.email,
    estadoCivil: estadoCivil!,
    possuiUniaoEstavel: possuiUniaoEstavel,
    regimeCasamento: regimeCasamento ?? null,
    maiorDe18Anos: true,
    tipoDoCliente: tipoDeCliente.Contato,
  }
}

const mapResponsavelEmpresa = (comprador: CompradorPessoaJuridicaSchemaOutput): TCreateCadastrarClienteData => {
  const responsavelEmpresa = comprador.responsavel
  if (!responsavelEmpresa) throw new Error('Responsável da empresa não encontrado')

  const { estadoCivil, possuiUniaoEstavel, regimeCasamento } = normalizarEstadoCivil(responsavelEmpresa.estadoCivil)

  return {
    cpf: responsavelEmpresa.dados.cpf.replaceAll(/\D/g, ''),
    nome: responsavelEmpresa.dados.nome,
    nacionalidadeId: responsavelEmpresa.dados.nacionalidadeId,
    profissaoId: responsavelEmpresa.dados.profissaoId,
    telefone: formatarTelefonePrincipal(responsavelEmpresa.contato.telefone),
    email: responsavelEmpresa.contato.email,
    estadoCivil: estadoCivil!,
    possuiUniaoEstavel: possuiUniaoEstavel,
    regimeCasamento: regimeCasamento ?? null,
    maiorDe18Anos: true,
    tipoDoCliente: tipoDeCliente.Contato,
  }
}

const mapVinculo = (comprador: CompradorVinculoStepSchemaOutput): TCreateCadastrarClienteData => {
  const { estadoCivil, possuiUniaoEstavel, regimeCasamento } = normalizarEstadoCivil(comprador.estadoCivil)

  return {
    cpf: comprador.dados.cpf.replaceAll(/\D/g, ''),
    nome: comprador.dados.nome,
    nacionalidadeId: comprador.dados.nacionalidadeId,
    profissaoId: comprador.dados.profissaoId,
    telefone: formatarTelefonePrincipal(comprador.contato.telefone),
    email: comprador.contato.email,
    estadoCivil: estadoCivil!,
    possuiUniaoEstavel: possuiUniaoEstavel,
    regimeCasamento: regimeCasamento ?? null,
    maiorDe18Anos: true,
    tipoDoCliente: tipoDeCliente.Contato,
  }
}

const STRATEGIES: Record<TPapelCompradorPF, StrategyFn> = {
  [PapelCompradorPF.COMPRADOR]: mapComprador,
  [PapelCompradorPF.CONJUGE]: mapConjuge,
  [PapelCompradorPF.MAE_PAI]: mapMaePai,
} as const

export const toClienteDto = (
  comprador:
    | CompradorPessoaFisicaStepSchemaOutput
    | CompradorPessoaJuridicaSchemaOutput
    | CompradorVinculoStepSchemaOutput,
  papel: TPapelComprador,
): TCreateCadastrarClienteData => {
  if (comprador.tipo === 'PessoaFisica') {
    const strategy = STRATEGIES[papel as TPapelCompradorPF]
    if (!strategy) throw new Error(`Papel não suportado: ${papel}`)
    return strategy(comprador)
  }
  if (comprador.tipo === 'PessoaJuridica') return mapResponsavelEmpresa(comprador)
  return mapVinculo(comprador)
}
